import React from 'react';
import { NavLink, useLocation, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ChartBar, 
  Question, 
  Users, 
  Folders,
  SignOut,
  CaretLeft,
  Gear,
  CreditCard,
  Book,
  Funnel
} from '@phosphor-icons/react';
import { cn } from '../lib/utils';

const adminNavItems = [
  { path: '/admin', icon: ChartBar, label: 'Dashboard', end: true },
  { path: '/admin/questions', icon: Question, label: 'Questions' },
  { path: '/admin/rule-links', icon: Book, label: 'HC Links' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/content', icon: Folders, label: 'Content' },
  { path: '/admin/settings', icon: CreditCard, label: 'Payments' },
  { path: '/admin/paywall', icon: Funnel, label: 'Paywall' },
];

export const AdminLayout = () => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border flex flex-col" data-testid="admin-sidebar">
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Gear weight="fill" className="w-6 h-6 text-primary" />
            <span className="font-heading font-bold text-lg">Admin Panel</span>
          </div>
        </div>

        {/* Back to App */}
        <NavLink
          to="/dashboard"
          className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground hover:bg-muted transition-colors border-b border-border"
        >
          <CaretLeft className="w-4 h-4" />
          Back to App
        </NavLink>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          <ul className="space-y-1 px-2">
            {adminNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.end 
                ? location.pathname === item.path
                : location.pathname.startsWith(item.path);
              
              return (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    end={item.end}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200",
                      isActive 
                        ? "bg-primary text-primary-foreground" 
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                    data-testid={`admin-nav-${item.label.toLowerCase()}`}
                  >
                    <Icon weight={isActive ? "fill" : "regular"} className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-sm bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-bold">A</span>
            </div>
            <div>
              <p className="font-medium text-sm">{user.name}</p>
              <p className="text-xs text-muted-foreground">Administrator</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted rounded-sm transition-colors"
          >
            <SignOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
};
